import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { CheckCircle2, Mail, Compass, HeartHandshake, ShieldCheck } from "lucide-react";
import { PENDING_VERIFY_EMAIL_KEY } from "@/lib/verify-status";

export const Route = createFileRoute("/account-created")({
  ssr: false,
  validateSearch: (search: Record<string, unknown>): { email?: string } => ({
    email: typeof search.email === "string" ? search.email : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Your account is created — Recovery Trauma™" },
      {
        name: "description",
        content:
          "Your Recovery Trauma™ account is created. Confirm your email address, then explore at your own pace or continue to membership whenever you're ready.",
      },
      { property: "og:title", content: "Your account is created — Recovery Trauma™" },
      {
        property: "og:description",
        content:
          "Your Recovery Trauma™ account is created. Confirm your email, then move at your own pace.",
      },
      { property: "og:type", content: "website" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AccountCreatedPage,
});

function AccountCreatedPage() {
  const { email: emailParam } = Route.useSearch();
  const [email, setEmail] = useState<string | null>(emailParam ?? null);

  useEffect(() => {
    window.scrollTo({ top: 0 });
    if (email) return;
    try {
      const stored = sessionStorage.getItem(PENDING_VERIFY_EMAIL_KEY);
      if (stored) setEmail(stored);
    } catch {
      /* storage unavailable — the page still works without the address */
    }
  }, [email]);

  return (
    <div className="min-h-[100svh] bg-gradient-to-b from-background via-background to-[var(--gold-soft)]/25 px-4 py-12 sm:py-20">
      <div className="mx-auto w-full max-w-xl">
        <div className="rounded-2xl border border-gold/30 bg-card p-6 shadow-[var(--shadow-gold)] sm:p-8">
          <div className="flex items-center gap-3">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-gold/15 text-[var(--gold-deep)]">
              <CheckCircle2 className="h-5 w-5" />
            </span>
            <div>
              <h1 className="font-display text-xl text-foreground sm:text-2xl">
                Your account is created
              </h1>
              <p className="text-sm text-muted-foreground">
                Welcome to Recovery Trauma™ — there's no rush from here.
              </p>
            </div>
          </div>

          <div className="mt-6 rounded-xl border border-gold/20 bg-gold/5 p-4">
            <div className="flex items-start gap-3">
              <Mail className="mt-0.5 h-4 w-4 shrink-0 text-[var(--gold-deep)]" />
              <div className="text-sm text-foreground">
                <p className="font-medium">One small step: confirm your email</p>
                <p className="mt-1 text-muted-foreground">
                  {email ? (
                    <>
                      We've sent a confirmation link to{" "}
                      <span className="font-medium text-foreground">{email}</span>. Opening
                      it keeps your account and your private writing safely yours.
                    </>
                  ) : (
                    <>
                      We've sent you a confirmation link. Opening it keeps your account and
                      your private writing safely yours.
                    </>
                  )}
                </p>
                <Link
                  to="/verify-email"
                  className="mt-3 inline-flex min-h-[44px] items-center rounded-lg border border-gold/50 px-4 text-sm font-medium text-[var(--gold-deep)] transition hover:bg-gold/10"
                >
                  Resend or change my email address
                </Link>
              </div>
            </div>
          </div>

          <h2 className="mt-8 font-display text-base text-foreground">
            What you can do next
          </h2>
          <ul className="mt-3 space-y-3">
            <li className="flex items-start gap-3 rounded-xl border border-border bg-background/60 p-4">
              <Compass className="mt-0.5 h-4 w-4 shrink-0 text-[var(--gold-deep)]" />
              <div className="text-sm">
                <p className="font-medium text-foreground">Explore at your own pace</p>
                <p className="text-muted-foreground">
                  Browse the Learning Hub and self-assessments, with several full resources
                  open to everyone.
                </p>
                <Link
                  to="/explore"
                  className="mt-2 inline-block font-medium text-[var(--gold-deep)] hover:underline"
                >
                  Start exploring
                </Link>
              </div>
            </li>
            <li className="flex items-start gap-3 rounded-xl border border-border bg-background/60 p-4">
              <HeartHandshake className="mt-0.5 h-4 w-4 shrink-0 text-[var(--gold-deep)]" />
              <div className="text-sm">
                <p className="font-medium text-foreground">Continue to membership</p>
                <p className="text-muted-foreground">
                  Whenever you're ready — journals, guided practices, community and live
                  workshops.
                </p>
                <Link
                  to="/membership"
                  className="mt-2 inline-block font-medium text-[var(--gold-deep)] hover:underline"
                >
                  See membership
                </Link>
              </div>
            </li>
          </ul>

          <p className="mt-6 flex items-start gap-2 text-xs text-muted-foreground">
            <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            Recovery Trauma™ is a wellbeing and education space, not therapy or crisis care.
            Anything you write in your journals stays private to you.
          </p>
        </div>

        <p className="mt-6 text-center text-sm text-muted-foreground">
          Already confirmed?{" "}
          <Link to="/login" className="font-medium text-[var(--gold-deep)] hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
